import React, { useState } from "react";

type FormGroupProps = {
  id: string;
  label: string;
  type?: string;
  classes?: string;
  input_classes?: string;
  placeholder?: string;
  handleChange?: any;
  value: string;
  autoComplete?: boolean | string;
  onBlur?: any;
  disabled?: boolean;
  readonly?: boolean;
  help?: HelpType | false;
  required?: boolean;
};
type HelpType = {
  classes?: string;
  txt?: string;
};
export const FormGroup: React.FC<FormGroupProps> = ({
  id,
  label,
  type = "text",
  classes = "",
  input_classes = "",
  placeholder,
  handleChange,
  value,
  autoComplete,
  onBlur,
  disabled = false,
  readonly = false,
  help = false,
  required = false,
}) => {
  const helpId = help ? `${id}-help` : undefined;
  const getAutoComplete = () => {
    if (typeof autoComplete === "string") return autoComplete;
    if (autoComplete === false) return "off";
    return undefined;
  };
  return (
    <div className={`form-group ${classes}`}>
      {label && <label htmlFor={id}>{label}{required && <span aria-hidden="true"> *</span>}</label>}
      {readonly ? (
        <p className={` ${input_classes}`}>{value}</p>
      ) : (
        <input
          type={type}
          id={id}
          onChange={handleChange}
          onBlur={onBlur}
          autoComplete={getAutoComplete()}
          disabled={disabled}
          className={`form-control ${input_classes}`}
          placeholder={placeholder}
          value={value}
          required={required}
          aria-describedby={helpId}
        />
      )}
      {help ? (
        <small id={helpId} className={`help form-text ${help.classes}`}>{help.txt}</small>
      ) : null}
    </div>
  );
};

type CheckBoxProps = {
  id: string;
  label: string;
  checked?: boolean | null;
  classes?: string;
  value?: string;
  input_classes?: string;
  handleChange?: React.ChangeEventHandler<HTMLInputElement>;
  disabled?: boolean;
  reverted?: boolean;
  help?: string;
};

export const CheckBox: React.FC<CheckBoxProps> = ({
  id,
  label,
  classes = "",
  input_classes = "",
  handleChange,
  value,
  disabled = false,
  checked = false,
  reverted = false,
  help,
}) => {
  let is_checked: boolean;
  if (reverted && checked === null) {
    is_checked = true;
  } else if (reverted) {
    is_checked = !checked;
  } else {
    is_checked = checked ?? false;
  }

  return (
    <div className={`form-check ${classes}`}>
      <input
        className={`form-check-input ${input_classes}`}
        data-reverted={reverted}
        type="checkbox"
        name={id}
        value={value || id}
        checked={is_checked}
        onChange={handleChange}
        id={id}
        disabled={disabled}
      />
      <label className="form-check-label" htmlFor={id}>
        {label}
      </label>
      {help ? <small className="form-text opacity_8">{help}</small> : null}
    </div>
  );
};

export const InputGroup: React.FC<{
  initial_value: string;
  classes?: string;
  title?: string;
  help?: string;
  handleUpdate?: (value: string | null) => void;
  loading?: boolean;
  id?: string;
  value_label?: string;
  error?: string;
}> = ({
  initial_value,
  classes = "",
  title,
  help,
  handleUpdate,
  loading,
  id,
  value_label,
  error,
}) => {
  const [value, setValue] = useState(initial_value);
  const handleType = (ev: { currentTarget: HTMLInputElement }) => {
    const value = ev.currentTarget.value;
    setValue(value);
  };
  return (
    <div className={`input-group-container ${classes}`}>
      {title && <h6 className="section-title">{title}</h6>}
      {help && <small className="form-text opacity_8">{help}</small>}
      <div className="input-group input-group-custom mt-2">
        <input
          type="number"
          className="form-control form-control-dark"
          id={id}
          onChange={handleType}
          value={value}
          min="1"
          max={50}
        />
        <div className="input-group-append">
          <span className="input-group-text input-group-label">
            {value_label || "days"}
          </span>
        </div>
        {handleUpdate ? (
          <div className="input-group-append ml-2">
            <button
              className={`btn ${
                loading
                  ? "btn-primary"
                  : value === "Saved!"
                    ? "btn-success"
                    : "btn-primary"
              } btn-save btn-no-shrink`}
              type="button"
              onClick={() => handleUpdate && handleUpdate(value)}
              disabled={loading}
            >
              {loading ? (
                <i
                  className="fal fa-spinner-third fa-spin"
                  aria-hidden="true"
                ></i>
              ) : value === "Saved!" ? (
                <>
                  Saved! <i className="fal fa-check" aria-hidden="true"></i>
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        ) : null}
      </div>
      {error && <small className="error text-danger ">{error}</small>}
    </div>
  );
};
